import { useEffect, useRef, useState, type JSX } from 'react';
import { useOutletContext } from 'react-router-dom';
import type { MeResponse, ParishSummary } from '../../api-client';

interface FrameContext {
  me: MeResponse;
  activeParish: ParishSummary | null;
}

export interface ParishIdentityData {
  id: string;
  Account_Name: string;
  Phone?: string;
  Email?: string;
  Billing_Street?: string;
  Billing_City?: string;
  Billing_Code?: string;
  Billing_Country?: string;
  Trading_Name?: string;
  Parish_Status?: string;
  Affiliation_Date?: string;
  Website_URL?: string;
  Incorporated_Society_Number?: string;
  Charities_Registration_Number?: string;
  IRD_Number?: string;
  GST_Registered?: boolean;
  Charities_Reporting_Tier?: string;
  Financial_Year_End?: string;
}

export function IdentityPage(): JSX.Element {
  const { activeParish } = useOutletContext<FrameContext>();
  const [data, setData] = useState<ParishIdentityData | null>(null);
  const [draft, setDraft] = useState<ParishIdentityData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [history, setHistory] = useState<ParishIdentityData[]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [financialYearEndInput, setFinancialYearEndInput] = useState('');
  const [savedField, setSavedField] = useState<string | null>(null);
  const [validationAlert, setValidationAlert] = useState<{ field: string; message: string } | null>(
    null,
  );
  const [sessionExpired, setSessionExpired] = useState(false);
  const incSocietyNumberRef = useRef<HTMLInputElement>(null);
  const irdNumberRef = useRef<HTMLInputElement>(null);
  const okButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    fetch('/api/me', { credentials: 'include' })
      .then((res) => {
        if (!res.ok) setSessionExpired(true);
      })
      .catch(() => setSessionExpired(true));
  }, []);

  useEffect(() => {
    if (!activeParish) return;
    setLoading(true);
    setError(null);
    fetch(`/api/parishes/${activeParish.id}/identity`, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((identity: ParishIdentityData) => {
        setData(identity);
        setDraft(identity);
        setHistory([identity]);
        setHistoryIndex(0);
        if (identity.Financial_Year_End) {
          const [, dd, mm] = identity.Financial_Year_End.match(/(\d{2})\/(\d{2})/) || [];
          if (mm && dd) {
            setFinancialYearEndInput(`${dd}/${mm}`);
          }
        }
      })
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [activeParish]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent): void => {
      if (data && draft && JSON.stringify(data) !== JSON.stringify(draft)) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [data, draft]);

  useEffect(() => {
    if (!validationAlert || !okButtonRef.current) {
      return;
    }
    okButtonRef.current.focus();
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        closeValidationAlert();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [validationAlert]);

  if (loading)
    return (
      <div className="mx-auto max-w-3xl px-6 py-4 text-sm text-ccaanz-grey">
        Loading parish identity...
      </div>
    );
  if (error)
    return (
      <div className="mx-auto max-w-3xl px-6 py-4">
        <div className="rounded-control border border-ccaanz-red bg-ccaanz-red-light px-4 py-3 text-sm text-ccaanz-red">
          Error: {error}
        </div>
      </div>
    );
  if (!data || !draft)
    return (
      <div className="mx-auto max-w-3xl px-6 py-4 text-sm text-ccaanz-grey">No data found</div>
    );

  const hasChanges = JSON.stringify(data) !== JSON.stringify(draft);

  const pushToHistory = (newDraft: ParishIdentityData) => {
    const newHistory = history.slice(0, historyIndex + 1);
    if (newHistory.length >= 30) {
      newHistory.shift();
    }
    newHistory.push(newDraft);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
  };

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      const previous = history[newIndex];
      if (previous) {
        setHistoryIndex(newIndex);
        setDraft(previous);
      }
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      const next = history[newIndex];
      if (next) {
        setHistoryIndex(newIndex);
        setDraft(next);
      }
    }
  };

  const autoSave = (updatedDraft: ParishIdentityData, fieldName: string | null = null) => {
    if (!activeParish) return;
    const parishId = activeParish.id;
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        };
        const bodyToSend = { ...updatedDraft };
        const res = await fetch(`/api/parishes/${parishId}/identity`, {
          method: 'POST',
          headers,
          credentials: 'include',
          body: JSON.stringify(bodyToSend),
        });
        if (res.ok) {
          const result = await res.json();
          const mergedIdentity = { ...updatedDraft, ...result.identity };
          setData(mergedIdentity);
          setDraft(mergedIdentity);
          if (fieldName) {
            setSavedField(fieldName);
            setTimeout(() => setSavedField(null), 1500);
          }
          if (mergedIdentity.Financial_Year_End) {
            const [, dd, mm] = mergedIdentity.Financial_Year_End.match(/(\d{2})\/(\d{2})/) || [];
            if (mm && dd) {
              setFinancialYearEndInput(`${dd}/${mm}`);
            }
          }
        }
      } catch (err) {
        console.error('Autosave failed:', err);
      }
    }, 1000);
  };

  const handleTextChange = (field: keyof ParishIdentityData, value: string) => {
    const updated = { ...draft, [field]: value || undefined };
    pushToHistory(updated);
    setDraft(updated);
  };

  const handleNumberChange = (field: keyof ParishIdentityData, value: string) => {
    const hasNonDigits = /[^\d]/.test(value);
    if (hasNonDigits) {
      const fieldName =
        field === 'Incorporated_Society_Number' ? 'Incorporated Society Number' : 'IRD Number';
      setValidationAlert({ field, message: `${fieldName} can only contain numbers` });
      return;
    }
    const numericValue = value.replace(/\D/g, '');
    const updated = { ...draft, [field]: numericValue || undefined };
    pushToHistory(updated);
    setDraft(updated);
  };

  const handleBoolChange = (field: keyof ParishIdentityData, value: boolean) => {
    const updated = { ...draft, [field]: value };
    pushToHistory(updated);
    setDraft(updated);
  };

  const handleDateChange = (field: keyof ParishIdentityData, value: string) => {
    const updated = { ...draft, [field]: value || undefined };
    pushToHistory(updated);
    setDraft(updated);
  };

  const handleFieldBlur = (field: keyof ParishIdentityData) => {
    autoSave(draft, String(field));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeParish) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      };
      const bodyToSend = { ...draft };
      const res = await fetch(`/api/parishes/${activeParish.id}/identity`, {
        method: 'POST',
        headers,
        credentials: 'include',
        body: JSON.stringify(bodyToSend),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || `HTTP ${res.status}`);
      }

      const result = await res.json();
      const mergedIdentity = { ...draft, ...result.identity };
      setData(mergedIdentity);
      setDraft(mergedIdentity);
      if (mergedIdentity.Financial_Year_End) {
        const [, mm, dd] = mergedIdentity.Financial_Year_End.match(/(\d{4})-(\d{2})-(\d{2})/) || [];
        if (mm && dd) {
          setFinancialYearEndInput(`${dd}-${mm}`);
        }
      }
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setSubmitting(false);
    }
  };

  const closeValidationAlert = () => {
    setValidationAlert(null);
    if (validationAlert?.field === 'Incorporated_Society_Number' && incSocietyNumberRef.current) {
      incSocietyNumberRef.current.focus();
    } else if (validationAlert?.field === 'IRD_Number' && irdNumberRef.current) {
      irdNumberRef.current.focus();
    }
  };

  const parishName = activeParish?.name || data.Trading_Name || data.Account_Name || 'Parish';
  const churchType = activeParish?.church_type || data.Parish_Status || null;

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-2xl font-semibold text-ccaanz-black">{parishName}</h2>
        <p className="text-sm text-ccaanz-grey">
          {churchType ? `${churchType} · Identity` : 'Identity module'}
        </p>
        {hasChanges && <p className="text-sm text-ccaanz-amber mt-2">You have unsaved changes</p>}
        {submitError && <p className="text-sm text-ccaanz-red mt-2">Error: {submitError}</p>}
      </header>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Church Details */}
        <section className="panel">
          <h3 className="section-heading">Church Details</h3>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <label htmlFor="church_name" className="input-label">
                Church Name
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  id="church_name"
                  type="text"
                  value={draft.Trading_Name ?? ''}
                  onChange={(e) => handleTextChange('Trading_Name', e.target.value)}
                  onBlur={() => handleFieldBlur('Trading_Name')}
                  className="input flex-1"
                />
                {savedField === 'Trading_Name' && (
                  <span className="text-sm text-ccaanz-green">✓</span>
                )}
              </div>
            </div>
            <div>
              <label htmlFor="legal_name" className="input-label">
                Legal Name
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  id="legal_name"
                  type="text"
                  value={draft.Account_Name ?? ''}
                  onChange={(e) => handleTextChange('Account_Name', e.target.value)}
                  onBlur={() => handleFieldBlur('Account_Name')}
                  className="input flex-1"
                />
                {savedField === 'Account_Name' && (
                  <span className="text-sm text-ccaanz-green">✓</span>
                )}
              </div>
            </div>
            <div>
              <label htmlFor="phone" className="input-label">
                Phone
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  id="phone"
                  type="tel"
                  value={draft.Phone ?? ''}
                  onChange={(e) => handleTextChange('Phone', e.target.value)}
                  onBlur={() => handleFieldBlur('Phone')}
                  className="input flex-1"
                />
                {savedField === 'Phone' && <span className="text-sm text-ccaanz-green">✓</span>}
              </div>
            </div>
            <div>
              <label htmlFor="email" className="input-label">
                Email
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  id="email"
                  type="email"
                  value={draft.Email ?? ''}
                  onChange={(e) => handleTextChange('Email', e.target.value)}
                  onBlur={() => handleFieldBlur('Email')}
                  className="input flex-1"
                />
                {savedField === 'Email' && <span className="text-sm text-ccaanz-green">✓</span>}
              </div>
            </div>
            <div className="col-span-2">
              <label htmlFor="website" className="input-label">
                Website
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  id="website"
                  type="text"
                  placeholder="https://example.com"
                  value={draft.Website_URL ?? ''}
                  onChange={(e) => handleTextChange('Website_URL', e.target.value)}
                  onBlur={() => handleFieldBlur('Website_URL')}
                  className="input flex-1"
                />
                {savedField === 'Website_URL' && (
                  <span className="text-sm text-ccaanz-green">✓</span>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Physical Address */}
        <section className="panel">
          <h3 className="section-heading">Physical Address</h3>
          <div className="space-y-3 mt-3">
            <div>
              <label htmlFor="street" className="input-label">
                Street
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  id="street"
                  type="text"
                  value={draft.Billing_Street ?? ''}
                  onChange={(e) => handleTextChange('Billing_Street', e.target.value)}
                  onBlur={() => handleFieldBlur('Billing_Street')}
                  className="input flex-1"
                />
                {savedField === 'Billing_Street' && (
                  <span className="text-sm text-ccaanz-green">✓</span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="city" className="input-label">
                  City
                </label>
                <div className="flex items-center gap-1.5">
                  <input
                    id="city"
                    type="text"
                    value={draft.Billing_City ?? ''}
                    onChange={(e) => handleTextChange('Billing_City', e.target.value)}
                    onBlur={() => handleFieldBlur('Billing_City')}
                    className="input flex-1"
                  />
                  {savedField === 'Billing_City' && (
                    <span className="text-sm text-ccaanz-green">✓</span>
                  )}
                </div>
              </div>
              <div>
                <label htmlFor="postcode" className="input-label">
                  Postcode
                </label>
                <div className="flex items-center gap-1.5">
                  <input
                    id="postcode"
                    type="text"
                    value={draft.Billing_Code ?? ''}
                    onChange={(e) => handleTextChange('Billing_Code', e.target.value)}
                    onBlur={() => handleFieldBlur('Billing_Code')}
                    className="input flex-1"
                  />
                  {savedField === 'Billing_Code' && (
                    <span className="text-sm text-ccaanz-green">✓</span>
                  )}
                </div>
              </div>
            </div>
            <div>
              <label htmlFor="country" className="input-label">
                Country
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  id="country"
                  type="text"
                  value={draft.Billing_Country ?? ''}
                  onChange={(e) => handleTextChange('Billing_Country', e.target.value)}
                  onBlur={() => handleFieldBlur('Billing_Country')}
                  className="input flex-1"
                />
                {savedField === 'Billing_Country' && (
                  <span className="text-sm text-ccaanz-green">✓</span>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Parish Status */}
        <section className="panel">
          <h3 className="section-heading">Parish Status</h3>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <label htmlFor="church_type" className="input-label">
                Church Type
              </label>
              <input
                id="church_type"
                type="text"
                value={draft.Parish_Status ?? ''}
                readOnly
                disabled
                className="input w-full cursor-not-allowed opacity-70"
              />{' '}
            </div>
            <div>
              <label htmlFor="affiliation_date" className="input-label">
                Affiliation Date
              </label>
              <input
                id="affiliation_date"
                type="date"
                value={draft.Affiliation_Date ? draft.Affiliation_Date.split('T')[0] : ''}
                readOnly
                disabled
                className="input w-full cursor-not-allowed opacity-70"
              />{' '}
            </div>
          </div>
        </section>

        {/* Legal Registrations */}
        <section className="panel">
          <h3 className="section-heading">Legal Registrations</h3>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <div>
              <label htmlFor="inc_society_number" className="input-label">
                Incorporated Society Number
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  ref={incSocietyNumberRef}
                  id="inc_society_number"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={draft.Incorporated_Society_Number ?? ''}
                  onChange={(e) =>
                    handleNumberChange('Incorporated_Society_Number', e.target.value)
                  }
                  onBlur={() => handleFieldBlur('Incorporated_Society_Number')}
                  className="input flex-1"
                />
                {savedField === 'Incorporated_Society_Number' && (
                  <span className="text-sm text-ccaanz-green">✓</span>
                )}
              </div>
            </div>
            <div>
              <label htmlFor="charities_number" className="input-label">
                Charities Registration Number
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  id="charities_number"
                  type="text"
                  value={draft.Charities_Registration_Number ?? ''}
                  onChange={(e) =>
                    handleTextChange('Charities_Registration_Number', e.target.value)
                  }
                  onBlur={() => handleFieldBlur('Charities_Registration_Number')}
                  className="input flex-1"
                />
                {savedField === 'Charities_Registration_Number' && (
                  <span className="text-sm text-ccaanz-green">✓</span>
                )}
              </div>
            </div>
            <div>
              <label htmlFor="ird_number" className="input-label">
                IRD Number
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  ref={irdNumberRef}
                  id="ird_number"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  value={draft.IRD_Number ?? ''}
                  onChange={(e) => handleNumberChange('IRD_Number', e.target.value)}
                  onBlur={() => handleFieldBlur('IRD_Number')}
                  className="input flex-1"
                />
                {savedField === 'IRD_Number' && (
                  <span className="text-sm text-ccaanz-green">✓</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 pt-3">
              <input
                id="gst_registered"
                type="checkbox"
                checked={draft.GST_Registered ?? false}
                onChange={(e) => handleBoolChange('GST_Registered', e.target.checked)}
                onBlur={() => handleFieldBlur('GST_Registered')}
                className="w-4 h-4 cursor-pointer"
              />
              <label htmlFor="gst_registered" className="text-sm text-ccaanz-black cursor-pointer">
                GST Registered
              </label>
              {savedField === 'GST_Registered' && (
                <span className="text-sm text-ccaanz-green">✓</span>
              )}
            </div>
            <div>
              <label htmlFor="charities_tier" className="input-label">
                Charities Reporting Tier
              </label>
              <div className="flex items-center gap-1.5">
                <select
                  id="charities_tier"
                  value={draft.Charities_Reporting_Tier ?? ''}
                  onChange={(e) => handleTextChange('Charities_Reporting_Tier', e.target.value)}
                  onBlur={() => handleFieldBlur('Charities_Reporting_Tier')}
                  className="input flex-1"
                >
                  <option value="">-None-</option>
                  <option value="Tier 1">Tier 1</option>
                  <option value="Tier 2">Tier 2</option>
                  <option value="Tier 3">Tier 3</option>
                  <option value="Tier 4">Tier 4</option>
                </select>
                {savedField === 'Charities_Reporting_Tier' && (
                  <span className="text-sm text-ccaanz-green">✓</span>
                )}
              </div>
            </div>
            <div>
              <label htmlFor="financial_year_end" className="input-label">
                Financial Year End (DD/MM)
              </label>
              <div className="flex items-center gap-1.5">
                <input
                  id="financial_year_end"
                  type="text"
                  placeholder="DD/MM"
                  value={financialYearEndInput}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFinancialYearEndInput(value);
                    if (!value) {
                      handleDateChange('Financial_Year_End', '');
                    } else if (/^\d{2}[-/]\d{2}$/.test(value)) {
                      const separator = value.includes('-') ? '-' : '/';
                      const [dd, mm] = value.split(separator);
                      handleDateChange('Financial_Year_End', `2000-${mm}-${dd}`);
                    } else {
                      const updated = { ...draft };
                      pushToHistory(updated);
                      setDraft(updated);
                    }
                  }}
                  onBlur={() => handleFieldBlur('Financial_Year_End')}
                  className="input flex-1"
                />
                {savedField === 'Financial_Year_End' && (
                  <span className="text-sm text-ccaanz-green">✓</span>
                )}
              </div>
            </div>
          </div>
        </section>

        <footer className="flex items-center justify-between gap-3 rounded-control border border-edge bg-paper-cream px-4 py-3">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={undo}
              disabled={historyIndex <= 0}
              className="btn btn-ghost"
            >
              Undo
            </button>
            <button
              type="button"
              onClick={redo}
              disabled={historyIndex >= history.length - 1}
              className="btn btn-ghost"
            >
              Redo
            </button>
          </div>
          <button type="submit" disabled={!hasChanges || submitting} className="btn btn-primary">
            {submitting ? 'Saving…' : 'Save Changes'}
          </button>
        </footer>
      </form>

      {validationAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-sm rounded-card bg-white p-7 shadow-card">
            <p className="mb-5 text-sm text-ccaanz-black">{validationAlert.message}</p>
            <div className="text-right">
              {/* eslint-disable jsx-a11y/no-autofocus -- deliberate: focused after an explicit user action (validation alert opening) */}
              <button
                ref={okButtonRef}
                onClick={closeValidationAlert}
                className="btn btn-primary"
                autoFocus
              >
                OK
              </button>
              {/* eslint-enable jsx-a11y/no-autofocus */}
            </div>
          </div>
        </div>
      )}

      {sessionExpired && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 w-full max-w-sm rounded-card bg-white p-7 shadow-card">
            <h3 className="mb-3 text-base font-semibold text-ccaanz-black">Session Ended</h3>
            <p className="mb-5 text-sm text-ccaanz-black">
              Your login session has expired. Please log in again to continue editing parish
              details.
            </p>
            <div className="text-right">
              <a href="/auth/login" className="btn btn-primary">
                Log In Again
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
